import { Modal } from "antd";
import { useMemo, useState } from "react";
import { t } from "ttag";

import { useListRevisionsQuery } from "metabase/api";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { Timeline } from "metabase/common/components/Timeline";
import { getTimelineEvents } from "metabase/common/components/Timeline/utils";
import CodeDiff from "metabase/jerry/CodeDiff";
import { useDispatch, useSelector } from "metabase/lib/redux";
import { PLUGIN_MODERATION } from "metabase/plugins";
import { revertToRevision } from "metabase/query_builder/actions";
import { getUser } from "metabase/selectors/user";
import type Question from "metabase-lib/v1/Question";
import type { Revision } from "metabase-types/api";

const { getModerationTimelineEvents } = PLUGIN_MODERATION;

const ALLOWED_DIFF_DESCRIPTIONS = [
  "modified the query.",
  // "created this.",
  // Add more descriptions here as needed
];

interface QuestionActivityTimelineProps {
  question: Question;
}

export function QuestionActivityTimeline({
  question,
}: QuestionActivityTimelineProps) {
  const {
    data: revisions,
    isLoading,
    error,
  } = useListRevisionsQuery({
    id: question.id(),
    entity: "card",
  });

  const currentUser = useSelector(getUser);
  const dispatch = useDispatch();
  const [viewedRevision, setViewedRevision] = useState<Revision | null>(null);

  const moderationReviews = question.getModerationReviews();

  const events = useMemo(() => {
    const moderationEvents = getModerationTimelineEvents(
      moderationReviews,
      currentUser,
    );
    const revisionEvents = getTimelineEvents({ revisions, currentUser });

    return [...revisionEvents, ...moderationEvents].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }, [moderationReviews, revisions, currentUser]);

  const { oldVal, newVal } = useMemo(() => {
    if (!viewedRevision?.diff) {
      return { oldVal: "", newVal: "" };
    }
    const { before, after } = viewedRevision.diff;

    const extract = (card: any) => {
      const q = card?.dataset_query;
      if (!q) {
        return "";
      }

      if (q.native?.query) {
        return q.native.query;
      }

      const stages = q?.stages;
      if (stages) {
        const firstStage = Array.isArray(stages)
          ? stages[0]
          : typeof stages.get === "function"
            ? stages.get(0)
            : undefined;

        if (firstStage) {
          const native =
            firstStage.native ||
            (typeof firstStage.get === "function"
              ? firstStage.get("native")
              : undefined);
          if (native) {
            return native;
          }
        }
      }

      if (q.query) {
        return JSON.stringify(q.query, null, 2) || "";
      }
      return JSON.stringify(q, null, 2) || "";
    };

    return {
      oldVal: extract(before),
      newVal: extract(after),
    };
  }, [viewedRevision]);

  if (isLoading || error) {
    return <LoadingAndErrorWrapper loading={isLoading} error={error} />;
  }

  return (
    <>
      <Timeline
        events={events}
        data-testid="saved-question-history-list"
        revert={(revision) =>
          dispatch(revertToRevision(question.id(), revision))
        }
        onViewChanges={(revision) => {
          if (ALLOWED_DIFF_DESCRIPTIONS.includes(revision.description)) {
            setViewedRevision(revision);
          }
        }}
        canViewRevision={(revision) =>
          ALLOWED_DIFF_DESCRIPTIONS.includes(revision.description)
        }
        canWrite={question.canWrite()}
        entity="card"
      />
      {viewedRevision && (
        <Modal
          open={!!viewedRevision}
          onCancel={(e) => {
            e.stopPropagation();
            setViewedRevision(null);
          }}
          title={t`History Changes`}
          width={"90%"}
          footer={null}
          styles={{
            body: {
              maxHeight: "70vh",
              overflowY: "auto",
            },
          }}
        >
          <CodeDiff diffType="sql" oldValue={oldVal} newValue={newVal} />
        </Modal>
      )}
    </>
  );
}
